"use client";

import React, { useEffect, useRef, useState } from "react";
import { cn } from "@/lib/utils";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { ArrowRight } from "lucide-react";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
const steps = [{
  id: 1,
  title: "Aanmelding en eerste contact",
  description: "U of uw verwijzer neemt contact met ons op om uw zorgvraag te bespreken. Wij luisteren aandachtig en plannen vervolgens op korte termijn een afspraak met u in."
}, {
  id: 2,
  title: "Kennismaking en inventarisatie",
  description: "Tijdens een persoonlijk gesprek bij u thuis brengen we uw situatie, wensen en behoeften in kaart. We kijken samen wat er nodig is voor een veilige en prettige thuissituatie."
}, {
  id: 3,
  title: "Afstemming van passende ondersteuning",
  description: "Op basis van de inventarisatie stellen we een persoonlijk zorgplan op. Hierin leggen we helder vast welke zorg en ondersteuning u precies gaat ontvangen."
}, {
  id: 4,
  title: "Inzet van zorgverleners",
  description: "Wij selecteren een vast team van gediplomeerde zorgprofessionals dat past bij uw situatie en achtergrond. Zo garanderen we continuïteit en vertrouwde gezichten over de vloer."
}, {
  id: 5,
  title: "Start van de zorg",
  description: "De afgesproken zorg en ondersteuning gaat van start volgens het zorgplan. Onze zorgverleners bieden u de afgesproken hulp met respect, deskundigheid en oprechte aandacht."
}, {
  id: 6,
  title: "Evaluatie en afstemming",
  description: "We evalueren regelmatig of de geboden zorg nog steeds passend is bij uw actuele situatie. Indien uw behoeften veranderen, passen we de ondersteuning in nauw overleg met u aan."
}];
function TimelineItem({
  step,
  index,
  isLast
}: {
  step: (typeof steps)[0];
  index: number;
  isLast: boolean;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.4
  });
  return <div ref={ref} className="relative pl-12 md:pl-24 pb-12 md:pb-16 last:pb-0 group" data-index={index}>
      {}
      {!isLast && <div className="absolute left-[1.1875rem] md:left-[2.1875rem] top-10 bottom-[-1rem] w-0.5 bg-border" />}

      {}
      {!isLast && <div className={cn("absolute left-[1.1875rem] md:left-[2.1875rem] top-10 bottom-[-1rem] w-0.5 origin-top transition-transform duration-1000 ease-out bg-primary", isVisible ? "scale-y-100" : "scale-y-0")} />}

      {}
      <div className={cn("absolute left-0 md:left-4 top-0 flex size-10 items-center justify-center rounded-full border-2 text-sm font-bold transition-all duration-700 z-10", isVisible ? "border-primary bg-primary text-primary-foreground shadow-md scale-110" : "border-border bg-background text-muted-foreground scale-100")}>
        {step.id}
      </div>

      {}
      <div className={cn("transition-all duration-700 delay-150 transform", isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8")}>
        <Card className="border-none shadow-sm hover:shadow-md transition-shadow duration-300 bg-card/60 backdrop-blur-sm">
          <CardContent className="p-6 md:p-8">
            <h3 className="text-xl md:text-2xl font-serif font-semibold text-foreground mb-3">
              {step.title}
            </h3>
            <p className="text-muted-foreground leading-relaxed">
              {step.description}
            </p>
          </CardContent>
        </Card>
      </div>
    </div>;
}
export default function VanAanmeldingTotPassendeZorg() {
  return <section id="van-aanmelding-tot-passende-zorg" className="bg-background py-16 md:py-24 overflow-hidden">
      <div className="container mx-auto px-4 md:px-6 max-w-4xl">
        <div className="text-center mb-16 md:mb-24">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-serif font-bold text-foreground mb-6">
            Van aanmelding tot passende zorg
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Wij geloven in een helder en zorgvuldig proces. Ontdek stap voor stap
            hoe wij samen met u de juiste zorg en ondersteuning in de thuissituatie
            realiseren.
          </p>
        </div>

        <div className="relative">
          {steps.map((step, index) => <TimelineItem key={step.id} step={step} index={index} isLast={index === steps.length - 1} />)}
        </div>

        <div className="mt-16 md:mt-20 text-center flex justify-center">
          <Button asChild size="lg" className="rounded-full px-8 h-12 text-base">
            <Link to="/ContactPage">
              Bespreek uw situatie <ArrowRight className="ml-2 size-5" />
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}